/**
 * Client-side media caps + video embed parse (mirrors API media-item.ts).
 */

export const MEDIA_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const MEDIA_VIDEO_MAX_BYTES = 100 * 1024 * 1024;
export const MEDIA_VIDEO_MAX_DURATION_MS = 120_000;
export const MEDIA_POST_MAX_IMAGES = 10;
export const MEDIA_POST_MAX_VIDEOS = 1;
export const MEDIA_COMMENT_MAX_IMAGES = 1;
export const MEDIA_MESSAGE_MAX_IMAGES = 10;
export const MEDIA_MESSAGE_MAX_FILES = 10;

export const POST_ATTACHMENT_MAX_COUNT = MEDIA_POST_MAX_IMAGES;

/**
 * @param {string} raw
 * @returns {{ type: 'video_embed', provider: 'youtube'|'vimeo', url: string, id: string } | null}
 */
export function parseVideoEmbedUrl(raw) {
  const input = String(raw || "").trim();
  if (!input) return null;
  let url;
  try {
    url = new URL(input);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  const host = url.hostname.replace(/^www\./, "").toLowerCase();

  if (host === "youtu.be") {
    const id = url.pathname.replace(/^\//, "").split("/")[0];
    if (id && /^[\w-]{6,}$/i.test(id)) {
      return {
        type: "video_embed",
        provider: "youtube",
        url: `https://www.youtube.com/watch?v=${id}`,
        id,
      };
    }
    return null;
  }

  if (
    host === "youtube.com" ||
    host === "m.youtube.com" ||
    host === "music.youtube.com"
  ) {
    const v = url.searchParams.get("v");
    if (v && /^[\w-]{6,}$/i.test(v)) {
      return {
        type: "video_embed",
        provider: "youtube",
        url: `https://www.youtube.com/watch?v=${v}`,
        id: v,
      };
    }
    const shorts = url.pathname.match(/^\/(?:embed|shorts|live)\/([\w-]{6,})/i);
    if (shorts?.[1]) {
      return {
        type: "video_embed",
        provider: "youtube",
        url: `https://www.youtube.com/watch?v=${shorts[1]}`,
        id: shorts[1],
      };
    }
    return null;
  }

  if (host === "vimeo.com" || host === "player.vimeo.com") {
    const m = url.pathname.match(/\/(?:video\/)?(\d+)/);
    if (m?.[1]) {
      return {
        type: "video_embed",
        provider: "vimeo",
        url: `https://vimeo.com/${m[1]}`,
        id: m[1],
      };
    }
  }

  return null;
}

/**
 * Read duration (ms) from a local video File via HTMLVideoElement.
 * @param {File} file
 * @returns {Promise<number>}
 */
export function readVideoDurationMs(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      const ms = Math.round((video.duration || 0) * 1000);
      if (!ms || !Number.isFinite(ms)) {
        reject(new Error("Could not read video duration"));
        return;
      }
      resolve(ms);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read video file"));
    };
    video.src = url;
  });
}

export function isImageFile(file) {
  const t = String(file?.type || "").toLowerCase();
  return t.startsWith("image/");
}

export function isVideoFile(file) {
  const t = String(file?.type || "").toLowerCase();
  return t === "video/mp4" || t === "video/webm";
}

/**
 * Collect image files from a clipboard paste event.
 * @param {ClipboardEvent} e
 * @returns {File[]}
 */
export function imageFilesFromPaste(e) {
  const items = e?.clipboardData?.items;
  if (!items?.length) return [];
  const out = [];
  for (const item of items) {
    if (item.kind === "file" && String(item.type || "").startsWith("image/")) {
      const f = item.getAsFile();
      if (f) out.push(f);
    }
  }
  return out;
}

/**
 * Find the first YouTube/Vimeo URL in free text / markdown and parse it.
 * @param {string} text
 * @returns {{ type: 'video_embed', provider: 'youtube'|'vimeo', url: string, id: string } | null}
 */
export function firstVideoEmbedFromText(text) {
  const raw = String(text || "");
  if (!raw.trim()) return null;
  // URLs in plain text or markdown links
  const re = /https?:\/\/[^\s)\]>'"]+/gi;
  let match;
  while ((match = re.exec(raw)) != null) {
    const candidate = match[0].replace(/[.,;:!?)]+$/, "");
    const parsed = parseVideoEmbedUrl(candidate);
    if (parsed) return parsed;
  }
  return null;
}
